import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  type ReactNode,
} from "react";

import {
  useBlocker,
  useLocation,
  type BlockerFunction,
} from "react-router-dom";

type BlockerArgs = Parameters<BlockerFunction>[0];

export interface NavigationGuard {
  /** True when this guard — not some other one — is holding a navigation. */
  isBlocked: boolean;
  /** Let the held navigation through. */
  proceed: () => void;
  /** Abandon the held navigation and stay put. */
  reset: () => void;
  /**
   * Exempt the next navigation from this guard. Call it immediately before
   * navigating on purpose — after deleting the record, or once the user has
   * already chosen to discard — so the prompt is not raised a second time.
   */
  allowNextNavigation: () => void;
}

const IDLE_GUARD: NavigationGuard = {
  isBlocked: false,
  proceed: () => {},
  reset: () => {},
  allowNextNavigation: () => {},
};

interface NavigationGuardContextValue {
  registerGuard: (id: string, shouldBlock: BlockerFunction) => () => void;
  blockedBy: string | null;
  /** The navigation being held, so the guard holding it can re-evaluate. */
  blockedArgs: BlockerArgs | null;
  proceed: () => void;
  reset: () => void;
}

const NavigationGuardContext = createContext<NavigationGuardContextValue>({
  registerGuard: () => () => {},
  blockedBy: null,
  blockedArgs: null,
  proceed: () => {},
  reset: () => {},
});

/**
 * Owns the app's single react-router blocker and multiplexes it across guards.
 *
 * The router only honours the last-registered blocker and silently ignores the
 * rest (see `shouldBlockNavigation` in @remix-run/router), so calling
 * `useBlocker` in more than one component means whichever mounted last wins and
 * the others quietly stop working. Every guard therefore registers here instead,
 * and this provider is the only `useBlocker` caller.
 *
 * Guards are consulted in registration order and the first one that wants to
 * block wins. In practice they are page-scoped and mutually exclusive.
 *
 * Must be mounted inside the data router — see `AppShell` in App.tsx.
 */
export const NavigationGuardProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const guardsRef = useRef(new Map<string, BlockerFunction>());
  // Both set while the router evaluates the blocker, which happens before the
  // re-render that surfaces the blocked state, so reading them during render is
  // safe and saves a second state update.
  const blockedByRef = useRef<string | null>(null);
  const blockedArgsRef = useRef<BlockerArgs | null>(null);
  // Held in a ref so registerGuard can stay stable — the router would otherwise
  // re-register every guard each time the blocker object changes identity.
  const blockerRef = useRef<ReturnType<typeof useBlocker> | null>(null);

  const registerGuard = useCallback(
    (id: string, shouldBlock: BlockerFunction) => {
      guardsRef.current.set(id, shouldBlock);
      return () => {
        guardsRef.current.delete(id);
        if (blockedByRef.current === id) {
          blockedByRef.current = null;
          blockedArgsRef.current = null;
          // The guard that raised the block is gone, so nothing is left to
          // render its prompt. Release the held navigation rather than leaving
          // the app permanently unable to navigate.
          blockerRef.current?.reset?.();
        }
      };
    },
    [],
  );

  // Stable so the router does not re-register the blocker on every render.
  const shouldBlock = useCallback<BlockerFunction>((args) => {
    for (const [id, guard] of guardsRef.current) {
      if (guard(args)) {
        blockedByRef.current = id;
        blockedArgsRef.current = args;
        return true;
      }
    }
    blockedByRef.current = null;
    blockedArgsRef.current = null;
    return false;
  }, []);

  const blocker = useBlocker(shouldBlock);
  blockerRef.current = blocker;

  const value = useMemo<NavigationGuardContextValue>(
    () => ({
      registerGuard,
      blockedBy: blocker.state === "blocked" ? blockedByRef.current : null,
      blockedArgs: blocker.state === "blocked" ? blockedArgsRef.current : null,
      proceed: () => blocker.proceed?.(),
      reset: () => blocker.reset?.(),
    }),
    [registerGuard, blocker],
  );

  return (
    <NavigationGuardContext.Provider value={value}>
      {children}
    </NavigationGuardContext.Provider>
  );
};

/**
 * Blocks in-app navigation while `shouldBlock` returns true, so a page can
 * confirm before the user leaves without wiring a handler into every link,
 * button and list row that could navigate away.
 *
 * Covers every navigation the router performs: sidebar and app bar links,
 * browser back/forward, any `navigate()` call, and search-param-only changes
 * such as opening a different bulletin in the alerts drawer. It does not cover
 * closing the tab — add a `beforeunload` listener for that where losing the
 * work actually matters.
 *
 * `shouldBlock` is read through a ref so it always sees current state, even when
 * a handler changes that state and navigates in the same tick.
 *
 * Render a confirmation while `isBlocked`, then call `proceed()` to let the
 * navigation through or `reset()` to stay. Note that `proceed()` replays only
 * the navigation — imperative side effects that ran alongside it are not
 * replayed, so any UI state involved (an open drawer, say) must be derived from
 * the URL rather than set imperatively.
 *
 * A block is released automatically if `shouldBlock` stops returning true while
 * it is held, so a prompt that never renders cannot wedge the whole app.
 *
 * Requires `NavigationGuardProvider` above it; without one the guard is inert.
 */
export function useNavigationGuard(shouldBlock: BlockerFunction) {
  const { registerGuard, blockedBy, blockedArgs, proceed, reset } = useContext(
    NavigationGuardContext,
  );
  const id = useId();
  const location = useLocation();

  const shouldBlockRef = useRef(shouldBlock);
  shouldBlockRef.current = shouldBlock;

  const allowNextRef = useRef(false);
  const allowNextNavigation = useCallback(() => {
    allowNextRef.current = true;
  }, []);

  // Disarm once the location has actually moved. An exemption is only ever
  // meant for the navigation it immediately precedes, but nothing guarantees
  // the guard gets consulted for it — the navigation may never happen, or an
  // earlier guard may block first and short-circuit this one. Clearing it here
  // stops a leftover exemption silently waving through an unrelated
  // navigation later on.
  useEffect(() => {
    allowNextRef.current = false;
  }, [location.key]);

  useEffect(
    () =>
      registerGuard(id, (args) => {
        if (allowNextRef.current) {
          allowNextRef.current = false;
          return false;
        }
        return shouldBlockRef.current(args);
      }),
    [id, registerGuard],
  );

  const isBlocked = blockedBy === id;

  // A block outlives the condition that raised it: the router holds the
  // navigation until proceed() or reset() is called, so a guard whose
  // condition clears afterwards — the edit got saved, the page it guards was
  // swapped for a spinner — would strand the app with no prompt on screen to
  // release it. Deliberately no dep array: re-check on every render of the
  // component owning the guard, which is what makes a cleared condition
  // visible here. Note this cancels the navigation rather than completing it,
  // so a stale block never turns into a jump the user did not ask for.
  useEffect(() => {
    if (!isBlocked || !blockedArgs) return;
    if (!shouldBlockRef.current(blockedArgs)) reset();
  });

  return useMemo<NavigationGuard>(
    () => ({
      isBlocked,
      proceed,
      reset,
      allowNextNavigation,
    }),
    [allowNextNavigation, isBlocked, proceed, reset],
  );
}

export { IDLE_GUARD };
